// Boost Blenz Admin — Decision Dashboard (Overview → under Daily Pulse).
// Ported from prototype/design_handoff_decision_dashboard. One card: toolbar →
// two-axis scrolling grid (sticky first column + two sticky header rows) → paginated
// footer. Daily / Weekly / Monthly / Yearly tabs read the same daily rows from
// /api/decision; the server sums additive columns and recomputes ratios per tab. Month
// filter locks on a row label and persists across day/week/month tabs (not yearly).
// Notes save per period key (server-side).

import React from "react";
import { OSWALD, FIGTREE, useT, useIsMobile } from "../theme";
import { Icon } from "../atoms";
import { useCachedJson } from "../datacache";

const money = (n: number | null | undefined) => (n == null ? "—" : "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const pct = (n: number | null | undefined) => (n == null ? "—" : n.toFixed(2) + "%");
const int = (n: number | null | undefined) => (n == null ? "—" : n.toLocaleString("en-US"));

// Design palette (specific to this dashboard — matched to the handoff spec).
const C = {
  headSticky: "#221b47", rowSticky: "#191338", noteCell: "#171232", cell: "#171232",
  grpSales: "#332a74", subSales: "#3f3489",
  grpShopify: "#1d4a3c", subShopify: "#23594a",
  grpMkt: "#5a3320", subMkt: "#6b3f27",
  grpRet: "#263c6e", subRet: "#2f4880",
  grp30: "#2a2450",
  border: "rgba(150,130,230,0.20)", borderSoft: "rgba(150,130,230,0.09)", divider: "rgba(150,130,230,0.14)",
  green: "#5fd39a", coral: "#ff9f8a", yellow: "#ffce5a", white: "#f4f1ff", body: "#e6dfff", faint: "#6f6592", orange: "#f8925c",
};

// Ad Spend %: green under 30, white 30–40, yellow 40–50, red over 50.
function adPctColor(n: number | null | undefined): string {
  if (n == null) return C.body;
  if (n < 30) return C.green;
  if (n <= 40) return C.white;
  if (n <= 50) return C.yellow;
  return C.coral;
}
// Blended ROAS vs the live KPI targets: green ≥ target-blended, white ≥ target,
// yellow ≥ breakeven, red below breakeven.
function roasColor(roas: number | null | undefined, rb: any): string {
  if (roas == null) return C.body;
  if (rb?.targetBlended != null && roas >= rb.targetBlended) return C.green;
  if (rb?.target != null && roas >= rb.target) return C.white;
  if (rb?.breakeven != null && roas >= rb.breakeven) return C.yellow;
  if (rb?.breakeven != null && roas < rb.breakeven) return C.coral;
  return C.white;
}

const relTime = (iso: string | null) => {
  if (!iso) return "—";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h} hour${h === 1 ? "" : "s"} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
};

const grpTh = (bg: string, span: number): React.CSSProperties => ({ position: "sticky", top: 0, zIndex: 4, background: bg, textAlign: "left", padding: "0 18px", height: 44, fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 12, color: "#fff" });
const subTh = (bg: string, color: string): React.CSSProperties => ({ position: "sticky", top: 44, zIndex: 4, background: bg, textAlign: "right", padding: "0 18px", height: 42, fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", fontSize: 11.5, color, whiteSpace: "nowrap" });

export function DecisionDashboardView() {
  const t = useT();
  const isMobile = useIsMobile();
  const [tab, setTab] = React.useState<"daily" | "weekly" | "monthly" | "yearly">("daily");
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(31);
  const [month, setMonth] = React.useState<string | null>(null);
  const [noteKey, setNoteKey] = React.useState<string | null>(null);
  const [noteDraft, setNoteDraft] = React.useState("");
  const [noteLabel, setNoteLabel] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const url = `/api/decision?tab=${tab}&page=${page}&size=${pageSize}${month ? `&month=${encodeURIComponent(month)}` : ""}`;
  const { data, loading, refresh } = useCachedJson<any>(url);

  const rows: any[] = data?.rows || [];
  const total = data?.total ?? 0;
  const pageCount = data?.pageCount ?? 1;
  const from = data?.from ?? 0;
  const unit = data?.unit || (tab === "yearly" ? "years" : tab === "monthly" ? "months" : tab === "weekly" ? "weeks" : "days");
  const svrPage = data?.page ?? page;
  const rb = data?.roasBands || {};
  const isDaily = tab === "daily";

  const switchTab = (nx: typeof tab) => { setTab(nx); setPage(1); if (nx === "yearly") setMonth(null); }; // a month filter can't apply to whole years
  const clearMonth = () => { setMonth(null); setPage(1); };
  const pickMonth = (mk: string) => { setMonth(mk); setPage(1); };

  const openNote = (key: string, label: string, existing: string | null) => { setNoteKey(key); setNoteLabel(label); setNoteDraft(existing || ""); };
  const closeNote = () => { setNoteKey(null); setNoteDraft(""); };
  const saveNote = async () => {
    if (!noteKey) return;
    setSaving(true);
    const r = await fetch("/api/decision/note", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ periodKey: noteKey, body: noteDraft }) }).catch(() => null);
    setSaving(false);
    if (r && r.ok) { closeNote(); refresh(); }
  };
  const deleteNote = async () => {
    if (!noteKey) return;
    setSaving(true);
    const r = await fetch("/api/decision/note", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ periodKey: noteKey, body: "" }) }).catch(() => null);
    setSaving(false);
    if (r && r.ok) { closeNote(); refresh(); }
  };

  // page window (5 buttons centered on current page)
  const win0 = Math.max(1, Math.min(svrPage - 2, pageCount - 4));
  const pageBtns: number[] = [];
  for (let p = win0; p < win0 + 5 && p <= pageCount; p++) pageBtns.push(p);

  const cell: React.CSSProperties = { background: C.cell, padding: "15px 18px", borderBottom: "1px solid " + C.borderSoft, textAlign: "right", fontSize: 14, color: C.body, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
  const divCell: React.CSSProperties = { ...cell, borderLeft: "1px solid " + C.divider };

  const dateHeader = tab === "yearly" ? "Year" : tab === "monthly" ? "Month" : tab === "weekly" ? "Week" : "Date";

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div>
        <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em", fontSize: isMobile ? 24 : 32, color: t.fg }}>Decision Dashboard</div>
        <div style={{ fontFamily: FIGTREE, fontSize: 13.5, color: t.muted, marginTop: 5 }}>Paid Ads Performance Overview</div>
      </div>

      <div style={{ border: "1px solid " + t.border, borderRadius: 20, background: "rgba(20,15,42,0.62)", overflow: "hidden" }}>
        {/* toolbar */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "14px 16px", borderBottom: "1px solid " + C.divider }}>
          <div style={{ display: "flex", alignItems: "center", gap: 2, background: "rgba(13,10,30,0.6)", border: "1px solid " + t.border, borderRadius: 999, padding: 4 }}>
            {(["daily", "weekly", "monthly", "yearly"] as const).map((x) => (
              <span key={x} onClick={() => switchTab(x)} style={{ fontFamily: OSWALD, fontWeight: 700, letterSpacing: "0.07em", fontSize: 11.5, padding: "8px 18px", borderRadius: 999, cursor: "pointer", textTransform: "uppercase", background: tab === x ? "#f4f1ff" : "transparent", color: tab === x ? "#1a1330" : t.muted }}>{x}</span>
            ))}
          </div>
          {month && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 9, padding: "7px 12px 7px 14px", borderRadius: 999, background: "rgba(248,146,92,0.14)", border: "1px solid rgba(248,146,92,0.42)", color: C.orange }}>
              <Icon name="funnel" size={13} color={C.orange} />
              <span style={{ fontFamily: OSWALD, fontWeight: 700, letterSpacing: "0.06em", fontSize: 12 }}>{month}</span>
              <span onClick={clearMonth} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 18, height: 18, borderRadius: 999, background: "rgba(13,10,30,0.5)", cursor: "pointer" }}><Icon name="x" size={10} color={C.orange} /></span>
            </span>
          )}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>
            <span style={{ width: 7, height: 7, borderRadius: 999, background: C.green }} />
            {loading && !data ? "Loading…" : `Updated ${relTime(data?.updatedAt)}`}
          </div>
        </div>

        {/* grid */}
        <div style={{ overflow: "auto", maxHeight: isMobile ? "70vh" : "min(660px, calc(100vh - 300px))" }}>
          <table style={{ borderCollapse: "separate", borderSpacing: 0, minWidth: isDaily ? 2330 : 2130, width: "100%", fontFamily: FIGTREE }}>
            <thead>
              <tr>
                <th rowSpan={2} style={{ position: "sticky", top: 0, left: 0, zIndex: 6, background: C.headSticky, textAlign: "left", padding: "0 18px", height: 44, minWidth: 172, fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 12, color: t.fg, borderRight: "1px solid " + C.border, borderBottom: "1px solid " + C.divider }}>{dateHeader}</th>
                <th rowSpan={2} style={{ position: "sticky", top: 0, zIndex: 4, background: C.headSticky, textAlign: "left", padding: "0 16px", minWidth: 150, fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 12, color: t.fg, borderRight: "1px solid " + C.border, borderBottom: "1px solid " + C.divider }}>Notes</th>
                <th colSpan={3} style={grpTh(C.grpSales, 3)}>Total Sales</th>
                <th colSpan={6} style={grpTh(C.grpShopify, 6)}>Shopify</th>
                <th colSpan={9} style={grpTh(C.grpMkt, 9)}>Marketing</th>
                <th colSpan={2} style={grpTh(C.grpRet, 2)}>Retention</th>
                {isDaily && <th rowSpan={2} style={{ position: "sticky", top: 0, zIndex: 4, background: C.grp30, textAlign: "left", padding: "0 18px", fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 12, color: t.fg2, whiteSpace: "nowrap", borderBottom: "1px solid " + C.divider }}>30-Day Ad Spend / Revenue</th>}
              </tr>
              <tr>
                {["Revenue", "Orders", "AOV"].map((h) => <th key={"s" + h} style={subTh(C.subSales, "#efe9ff")}>{h}</th>)}
                {["Revenue", "Orders", "New Customers", "New Orders", "New Revenue", "Ret. Customers"].map((h) => (
                  <th key={"sh" + h} style={subTh(C.subShopify, "#e6fff4")} title={h === "New Revenue" ? "Exact: revenue from first-time-customer orders, read from order line customerJourneySummary (index 1 = the customer's first order) and applied to Shopify's total revenue." : undefined}>
                    {h}
                  </th>
                ))}
                {["Meta Ad Spend", "Google Ad Spend", "Total Ad Spend", "Ad Spend %", "Blended ROAS", "New Emails", "New SMS", "Email Rev %", "Flows Rev %"].map((h) => (
                  <th key={"m" + h} style={subTh(C.subMkt, "#ffeee4")} title={h === "Email Rev %" ? "Campaign-attributed email revenue ÷ total revenue (Klaviyo)." : h === "Flows Rev %" ? "Flow-attributed email revenue ÷ total revenue (Klaviyo)." : undefined}>{h}</th>
                ))}
                <th style={subTh(C.subRet, "#e8f0ff")}>Unique Vis.</th>
                <th style={subTh(C.subRet, "#e8f0ff")} title="Returning customers ÷ all buyers (Shopify Analytics)">Returning %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const adC = adPctColor(r.adPct);
                const adPctStyle: React.CSSProperties = { color: adC, fontWeight: adC === C.white || adC === C.body ? 400 : 700 };
                const roasStyle: React.CSSProperties = { color: roasColor(r.roas, rb), fontWeight: 700 };
                return (
                  <tr key={r.key} className="dd-row">
                    <td style={{ position: "sticky", left: 0, zIndex: 2, background: C.rowSticky, padding: 0, borderRight: "1px solid " + C.border, borderBottom: "1px solid " + C.borderSoft }}>
                      <span onClick={tab === "yearly" ? undefined : () => pickMonth(r.monthKey)} title={tab === "yearly" ? undefined : "Filter to this month"} style={{ display: "flex", alignItems: "center", gap: 8, padding: "15px 18px", fontSize: 14, fontWeight: 600, color: t.fg, whiteSpace: "nowrap", cursor: tab === "yearly" ? "default" : "pointer" }}>
                        {r.label}{tab !== "yearly" && <Icon name="funnel" size={12} color={t.faint} style={{ opacity: 0.5 }} />}
                      </span>
                    </td>
                    <td style={{ background: C.noteCell, padding: "9px 16px", borderRight: "1px solid " + C.border, borderBottom: "1px solid " + C.borderSoft }}>
                      {r.note ? (
                        <span onClick={() => openNote(r.key, r.label, r.note)} style={{ display: "block", maxWidth: 126, padding: "7px 11px", borderRadius: 10, border: "1px solid rgba(248,146,92,0.4)", background: "rgba(248,146,92,0.1)", color: "#ffd0b3", fontSize: 12.5, lineHeight: 1.3, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.note}</span>
                      ) : (
                        <span onClick={() => openNote(r.key, r.label, null)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 999, border: "1px dashed rgba(150,130,230,0.4)", color: t.purple, fontFamily: OSWALD, fontWeight: 700, letterSpacing: "0.06em", fontSize: 11.5, textTransform: "uppercase", cursor: "pointer", whiteSpace: "nowrap" }}>+ Add note</span>
                      )}
                    </td>
                    {/* Total Sales */}
                    <td style={{ ...divCell, color: t.fg, fontWeight: 700 }}>{money(r.revenue)}</td>
                    <td style={cell}>{int(r.orders)}</td>
                    <td style={cell}>{money(r.aov)}</td>
                    {/* Shopify */}
                    <td style={divCell}>{money(r.revenue)}</td>
                    <td style={cell}>{int(r.orders)}</td>
                    <td style={cell}>{int(r.newCustomers)}</td>
                    <td style={cell}>{int(r.newOrders)}</td>
                    <td style={cell}>{money(r.newRevenue)}</td>
                    <td style={cell}>{int(r.retCustomers)}</td>
                    {/* Marketing */}
                    <td style={divCell}>{money(r.meta)}</td>
                    <td style={cell}>{money(r.google)}</td>
                    <td style={{ ...cell, color: t.fg, fontWeight: 600 }}>{money(r.totalAd)}</td>
                    <td style={cell}><span style={adPctStyle}>{pct(r.adPct)}</span></td>
                    <td style={cell}><span style={roasStyle}>{r.roas == null ? "—" : r.roas.toFixed(2)}</span></td>
                    <td style={cell}>{int(r.newEmails)}</td>
                    <td style={{ ...cell, color: C.faint }}>{r.newSms == null ? "—" : int(r.newSms)}</td>
                    <td style={cell}>{pct(r.emailRevPct)}</td>
                    <td style={cell}>{pct(r.flowRevPct)}</td>
                    {/* Retention */}
                    <td style={divCell}>{int(r.uniqueVis)}</td>
                    <td style={cell}>{pct(r.retRate)}</td>
                    {isDaily && <td style={{ ...divCell, color: t.muted }}>{pct(r.ad30)}</td>}
                  </tr>
                );
              })}
              {!rows.length && (
                <tr><td colSpan={isDaily ? 23 : 22} style={{ padding: "26px", textAlign: "center", fontFamily: FIGTREE, fontSize: 13, color: t.muted, background: C.cell }}>
                  {loading ? "Loading…" : data && data.ok === false ? "Couldn’t load the dashboard data." : month ? `No ${unit} in ${month}.` : "No data yet — the daily feed is still warming up."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* footer */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", padding: "12px 18px", borderTop: "1px solid " + C.divider, fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>
          <span>Showing {total === 0 ? 0 : from + 1}–{Math.min(from + pageSize, total)} of {total} {unit}{month ? ` in ${month}` : ""}</span>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontFamily: OSWALD, fontWeight: 700, letterSpacing: "0.09em", fontSize: 10.5, textTransform: "uppercase", color: t.faint }}>Rows</span>
            <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value, 10) || 31); setPage(1); }} style={{ appearance: "none", background: "rgba(13,10,30,0.6)", border: "1px solid " + t.borderStrong, borderRadius: 9, color: C.body, fontFamily: FIGTREE, fontSize: 12.5, padding: "7px 12px", cursor: "pointer", outline: "none" }}>
              {[14, 31, 60, 90].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </span>
          <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <PgBtn label="First" disabled={svrPage <= 1} onClick={() => setPage(1)} />
            <PgBtn label="Prev" disabled={svrPage <= 1} onClick={() => setPage(Math.max(1, svrPage - 1))} />
            {pageBtns.map((p) => (
              <span key={p} onClick={() => setPage(p)} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 32, height: 32, padding: "0 8px", borderRadius: 9, cursor: "pointer", fontSize: 12.5, fontWeight: p === svrPage ? 800 : 700, background: p === svrPage ? C.orange : "transparent", color: p === svrPage ? "#1a1330" : C.body, border: p === svrPage ? "none" : "1px solid " + t.borderStrong }}>{p}</span>
            ))}
            <PgBtn label="Next" disabled={svrPage >= pageCount} onClick={() => setPage(Math.min(pageCount, svrPage + 1))} />
            <PgBtn label="Last" disabled={svrPage >= pageCount} onClick={() => setPage(pageCount)} />
          </span>
        </div>
      </div>

      {/* note modal */}
      {noteKey && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: FIGTREE }}>
          <div onClick={closeNote} style={{ position: "absolute", inset: 0, background: "rgba(6,4,18,0.72)" }} />
          <div style={{ position: "relative", width: "min(520px,92vw)", background: "linear-gradient(180deg,#221b47 0%,#1b1440 100%)", border: "1px solid " + t.borderStrong, borderRadius: 20, boxShadow: "0 30px 80px rgba(0,0,0,0.6)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "18px 20px", borderBottom: "1px solid " + C.divider }}>
              <div>
                <div style={{ fontFamily: OSWALD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: 16, color: t.fg }}>Note</div>
                <div style={{ fontSize: 12.5, color: t.muted, marginTop: 2 }}>{noteLabel}</div>
              </div>
              <span onClick={closeNote} style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 999, border: "1px solid " + t.borderStrong, color: t.fg2, cursor: "pointer" }}><Icon name="x" size={14} /></span>
            </div>
            <div style={{ padding: "18px 20px 8px" }}>
              <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value.slice(0, 300))} maxLength={300} placeholder="What happened this period? Promo, outage, ad change…"
                style={{ width: "100%", boxSizing: "border-box", minHeight: 132, resize: "vertical", background: "rgba(13,10,30,0.6)", border: "1px solid " + t.borderStrong, borderRadius: 14, padding: "14px 15px", color: t.fg, fontFamily: FIGTREE, fontSize: 14.5, lineHeight: 1.55, outline: "none" }} />
              <div style={{ textAlign: "right", fontSize: 11.5, color: t.faint, marginTop: 6 }}>{noteDraft.length}/300</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "12px 20px 20px" }}>
              {rows.find((r) => r.key === noteKey)?.note && (
                <span onClick={deleteNote} style={{ display: "inline-flex", alignItems: "center", padding: "11px 18px", borderRadius: 999, border: "1px solid " + t.borderStrong, color: t.muted, fontFamily: OSWALD, fontWeight: 700, letterSpacing: "0.06em", fontSize: 12.5, textTransform: "uppercase", cursor: "pointer" }}>Delete</span>
              )}
              <span onClick={closeNote} style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "11px 22px", borderRadius: 999, border: "1px solid " + t.borderStrong, background: "transparent", color: t.fg2, fontFamily: OSWALD, fontWeight: 700, letterSpacing: "0.06em", fontSize: 12.5, textTransform: "uppercase", cursor: "pointer" }}>Cancel</span>
              <span onClick={saving ? undefined : saveNote} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 26px", borderRadius: 999, background: C.orange, color: "#1a1330", fontFamily: OSWALD, fontWeight: 700, letterSpacing: "0.06em", fontSize: 12.5, textTransform: "uppercase", cursor: saving ? "default" : "pointer", opacity: saving ? 0.7 : 1 }}><Icon name="check" size={14} />{saving ? "Saving…" : "Save note"}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PgBtn({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) {
  const t = useT();
  return (
    <span onClick={disabled ? undefined : onClick} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", height: 32, padding: "0 12px", borderRadius: 9, border: "1px solid " + (disabled ? "rgba(150,130,230,0.1)" : t.borderStrong), fontFamily: OSWALD, fontWeight: 700, letterSpacing: "0.06em", fontSize: 11, textTransform: "uppercase", cursor: disabled ? "default" : "pointer", color: disabled ? "#5a5178" : t.fg2 }}>{label}</span>
  );
}
